import { useMemo } from 'react';
import { usePlayPickems, useRecentPickems } from '@/api/query/usePickem';
import type { Competition } from '@/types/domain';
import { transformPickemToCompetition } from '@/utils/type-transformers';

export const useCompetitions = () => {
  const playQuery = usePlayPickems();
  const recentQuery = useRecentPickems();

  const recentPickems = recentQuery.data?.pickems || [];

  const competitionSets = useMemo(() => {
    const allCompetitions: Competition[] = [];
    const completedCompetitions: Competition[] = [];

    // Add play/active competitions (exclude final state)
    if (playQuery.data?.pickems) {
      playQuery.data.pickems.forEach(pickem => {
        const competition = transformPickemToCompetition(pickem);
        if (competition.state !== 'final') {
          allCompetitions.push(competition);
        } else {
          completedCompetitions.push(competition);
        }
      });
    }

    // Add recent/completed competitions to completed list
    recentPickems.forEach(pickem => {
      completedCompetitions.push(transformPickemToCompetition(pickem));
    });

    // Sort active competitions by date (newest first)
    allCompetitions.sort((a, b) => b.gameDate.getTime() - a.gameDate.getTime());

    // Sort completed competitions by date (most recent first)
    completedCompetitions.sort((a, b) => b.gameDate.getTime() - a.gameDate.getTime());

    return { active: allCompetitions, completed: completedCompetitions };
  }, [playQuery.data, recentPickems]);

  const getCompetition = (id: string): Competition | undefined => {
    return [...competitionSets.active, ...competitionSets.completed].find(comp => comp.id === id);
  };

  // Note: getCompetitionDetails should be used as a separate hook
  // Use usePickemById directly in components

  const getUpcomingCompetitions = (): Competition[] => {
    return competitionSets.active.filter(comp => comp.state === 'upcoming');
  };

  const getActiveCompetitions = (): Competition[] => {
    return competitionSets.active.filter(comp => ['countdown', 'live'].includes(comp.state));
  };

  const getCompletedCompetitions = (): Competition[] => {
    return competitionSets.completed;
  };

  const isLoading = playQuery.isLoading || recentQuery.isLoading;
  // True once both queries have completed at least one fetch. Distinct from
  // !isLoading, which is also true while the query is disabled (e.g. waiting
  // for user context). Use this when you need to know "data is ready".
  const isFetched = playQuery.isFetched && recentQuery.isFetched;

  const error = playQuery.error || recentQuery.error;

  const refetch = () => {
    playQuery.refetch();
    recentQuery.refetch();
  };

  return {
    competitions: [...competitionSets.active, ...competitionSets.completed], // Backwards compatibility
    activeCompetitions: competitionSets.active,
    completedCompetitions: competitionSets.completed,
    getCompetition,
    getUpcomingCompetitions,
    getActiveCompetitions,
    getCompletedCompetitions,
    isLoading,
    isFetched,
    error,
    refetch,
  };
};
